import React, { useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { useLocation, useNavigate } from "react-router-dom";
import { Disclosure, Transition } from "@headlessui/react";

import Breadcrumb from "../components/breadCrumb";
import { PhrasesContext } from "../context/phrasesContext";
import ThemeLayout from "../layouts/ThemeLayout";
import { getPendingPaymentServicesWithRequests } from "../services/Common";

import InvoicesWithRequstsSection from "../components/InvoicesWithRequstsSection";
import InvoicesPaymentSection from "../components/InvoicesPaymentSection";
import CaseDetailsSectionLatestMobile from "../components/case-tracking/CaseDetailsSectionLatestMobile";

const InvoiceDetails = () => {
  const { caseid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [lang, setLang] = useState("");
  const [loading, setLoading] = useState(true);
  const [enPhrase, arPhrase] = useContext(PhrasesContext);

  const [service, setService] = useState({});
  const [isInvoiceDetails, setIsInvoiceDetails] = useState(null);
  const [invoiceNumberId, SetInvoiceNumberId] = useState();

  // Track selections using generated unique keys.
  const [selectedRequests, setSelectedRequests] = useState([]);

  /**
   * Check whether the current URL contains payment callback information.
   *
   * We must not immediately redirect while processing a callback because
   * InvoicesPaymentSection needs to show the payment success popup first.
   */
  const hasPaymentCallback = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);

    return Boolean(
      searchParams.get("StatusCode") ||
      searchParams.get("statusCode") ||
      searchParams.get("statuscode") ||
      searchParams.get("serviceId") ||
      searchParams.get("ServiceId") ||
      searchParams.get("serviceID") ||
      searchParams.get("Callback") ||
      searchParams.get("callback") ||
      searchParams.get("ReturnUrl") ||
      searchParams.get("returnUrl") ||
      searchParams.get("PPId") ||
      searchParams.get("ppId") ||
      searchParams.get("PPID"),
    );
  }, [location.search]);

  useEffect(() => {
    const storedLang = window.localStorage.getItem("lang");

    const currentLang = storedLang === "en" ? "en" : "ar";

    document.body.dir = currentLang === "en" ? "ltr" : "rtl";

    setLang(currentLang);
    getServiceDetailsService(currentLang);
  }, [caseid]);

  /**
   * Load the pending-payment services for the current invoice.
   */
  const getServiceDetailsService = async (currentLang) => {
    setLoading(true);

    try {
      const res = await getPendingPaymentServicesWithRequests(
        caseid,
        currentLang,
      );

      console.log("Pending payment services response:", res);

      setService(res || {});
      setIsInvoiceDetails(res?.isSuccess === true);
      SetInvoiceNumberId(res?.data?.case?.invoiceNumber);
    } catch (error) {
      console.error("Failed to load invoice details:", error);

      setService({
        isSuccess: false,
        internalErrorCode: null,
        message:
          error?.message ||
          "An error occurred while loading the invoice details.",
        data: null,
      });

      setIsInvoiceDetails(false);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Redirect when the invoice no longer has pending-payment services.
   *
   * During a payment callback, the redirect is handled after the success
   * popup is closed.
   */
  useEffect(() => {
    if (loading || isInvoiceDetails === null) {
      return;
    }

    console.log("Invoice redirect validation:", {
      loading,
      isInvoiceDetails,
      hasPaymentCallback,
      pathname: location.pathname,
      search: location.search,
      responseMessage: service?.message,
    });

    if (hasPaymentCallback) {
      return;
    }

    if (isInvoiceDetails === false) {
      navigate("/invoices", {
        replace: true,
        state: {
          invoiceId: caseid,
          invoiceMessage:
            service?.message ||
            "No pending payment services found for this invoice.",
        },
      });
    }
  }, [
    loading,
    isInvoiceDetails,
    hasPaymentCallback,
    location.pathname,
    location.search,
    navigate,
    caseid,
    service?.message,
  ]);

  const requestsList = service?.data?.services || [];

  /**
   * Generate a unique key for each pending-payment service.
   */
  const getRequestUniqueKey = (request, index) => {
    if (request?.id) {
      return `service-${request.id}`;
    }

    if (request?.caseNumber) {
      return `${request.caseNumber}-${index}`;
    }

    return `request-index-${index}`;
  };

  /**
   * Select or deselect a single request.
   */
  const handleToggleRequest = (uniqueKey) => {
    setSelectedRequests((previousSelectedRequests) => {
      if (previousSelectedRequests.includes(uniqueKey)) {
        return previousSelectedRequests.filter(
          (currentKey) => currentKey !== uniqueKey,
        );
      }

      return [...previousSelectedRequests, uniqueKey];
    });
  };

  /**
   * Select or deselect all requests.
   */
  const handleToggleSelectAll = () => {
    if (!requestsList.length) {
      return;
    }

    if (selectedRequests.length === requestsList.length) {
      setSelectedRequests([]);
      return;
    }

    const allUniqueKeys = requestsList.map((request, index) =>
      getRequestUniqueKey(request, index),
    );

    setSelectedRequests(allUniqueKeys);
  };

  /**
   * Redirect after the success popup has been closed.
   */
  const handlePaymentSuccessClose = () => {
    window.sessionStorage.removeItem("temp_success_service_id");
    window.sessionStorage.removeItem("temp_success_invoice_id");

    navigate("/invoices", {
      replace: true,
      state: {
        paymentSuccess: true,
        invoiceId: caseid,
      },
    });
  };

  if (loading) {
    return (
      <ThemeLayout>
        <p className="text-[#b68a35] font-bold font-Almarai mt-[8px]">
          {lang === "en" ? "Loading..." : "جاري عرض البيانات..."}
        </p>
      </ThemeLayout>
    );
  }

  /**
   * While redirecting from an invalid invoice, avoid briefly rendering
   * an empty invoice-details page.
   */
  if (isInvoiceDetails === false && !hasPaymentCallback) {
    return (
      <ThemeLayout>
        <p className="text-[#b68a35] font-bold font-Almarai mt-[8px]">
          {lang === "en" ? "Redirecting..." : "جاري إعادة التوجيه..."}
        </p>
      </ThemeLayout>
    );
  }

  return (
    <ThemeLayout>
      <div className="flex flex-col w-full py-4 max-w-[1440px] px-[16px] md:px-[24px] lg:px-[0px]">
        <Breadcrumb
          title={
            lang === "en" ? enPhrase["lblMycases"] : arPhrase["lblMycases"]
          }
          subtitle1Href="9999"
          last={
            lang === "en" ? enPhrase["lblInvoices"] : arPhrase["lblInvoices"]
          }
          lastHref="/invoices"
          lastSecond={caseid}
        />

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-[16px] md:mb-[24px]">
          <div className="flex flex-col items-start gap-[8px]">
            <div className="flex flex-row items-center gap-[8px]">
              <h1 className="text-[18px] md:text-[20px] text-[#010202] font-Almarai leading-[22px]">
                {lang === "en"
                  ? enPhrase["lblOrderDues"]
                  : arPhrase["lblOrderDues"]}
              </h1>

              <p className="text-[20px] text-[#010202] font-Almarai leading-[22px]">
                {invoiceNumberId}
              </p>
            </div>

            <p className="text-[12px] text-[#8D867D] font-Almarai leading-[24px]">
              {lang === "en"
                ? enPhrase["lblOrderDuesText"]
                : arPhrase["lblOrderDuesText"]}
            </p>
          </div>

          <div className="flex flex-row items-center border border-[#3E3B37] bg-[#3E3B37] py-[2px] px-[10px] rounded-[40px] gap-[4px] my-[8px] md:my-0">
            <img src="/assets/img/invoices/FileText.svg" alt="" />

            <div className="flex flex-row items-center gap-[4px]">
              <span className="text-[12px] text-[#fff] font-Almarai font-normal">
                {requestsList.length}
              </span>

              <p className="text-[12px] text-[#fff] font-Almarai font-normal">
                {lang === "en"
                  ? enPhrase["lblRequests"]
                  : arPhrase["lblRequests"]}
              </p>
            </div>
          </div>
        </div>

        <Disclosure
          as="div"
          className="w-full bg-[#F3F0EA] border-[0.5px] border-[#E6D7A2] shadow-[0px_10px_40px_#361E1214] rounded-[8px] flex md:hidden flex-col items-center gap-[24px] mb-[12px] md:mb-0"
          defaultOpen={false}
        >
          {({ open }) => (
            <div className="w-full">
              <Disclosure.Button className="group flex w-full items-center justify-between px-[16px] h-[56px]">
                <p className="font-Almarai font-bold text-[14px] leading-[20px] text-[#361E12]">
                  {lang === "en"
                    ? enPhrase["lblCaseDetails"]
                    : arPhrase["lblCaseDetails"]}
                </p>

                <div className="flex items-center justify-center gap-2">
                  <img
                    src="/assets/img/add-request/dash.svg"
                    alt="dash"
                    className="w-[1px] h-[20px]"
                  />

                  <img
                    className={`size-5 transition-transform duration-500 ease-in-out ${
                      open ? "rotate-0" : "rotate-180"
                    }`}
                    src="/assets/img/add-request/ArrowUP.svg"
                    alt="arrow"
                  />
                </div>
              </Disclosure.Button>

              <Transition
                show={open}
                enter="transition-all duration-700 ease-in-out"
                enterFrom="max-h-0 opacity-0"
                enterTo="opacity-100"
                leave="transition-all duration-500 ease-in-out"
                leaveFrom="opacity-100"
                leaveTo="max-h-0 opacity-0"
                className="overflow-hidden flex items-center justify-center"
              >
                <Disclosure.Panel
                  static
                  className={`${
                    typeof window !== "undefined" && window.innerWidth <= 768
                      ? "mx-[4px] mb-[4px]"
                      : "mx-[8px] mb-[8px]"
                  } w-[99%] rounded-[12px] border-[0.5px] border-solid border-[#D4D2CE] bg-[#fff] shadow-[0px_10px_40px_#351F131F] p-[16px] gap-[16px] flex flex-col transition-all duration-500 ease-in-out overflow-visible`}
                >
                  <CaseDetailsSectionLatestMobile
                    service={service?.data?.case}
                    lang={lang}
                    arPhrase={arPhrase}
                    enPhrase={enPhrase}
                    IsInvoice
                  />
                </Disclosure.Panel>
              </Transition>
            </div>
          )}
        </Disclosure>

        <div className="flex flex-col md:flex-row items-start justify-between gap-[16px] md:gap-0">
          <div className="flex flex-col gap-[12px] md:gap-[32px] w-full md:w-[65%]">
            <div className="flex flex-row items-center justify-between py-[4px] px-[16px] rounded-[8px] border !border-[#F9F7ED] bg-[#F3F0EA]">
              <div className="font-Almarai text-[12px] leading-[24px] text-[#6F6A62] flex flex-row items-center gap-[4px]">
                <span>
                  {lang === "en"
                    ? enPhrase["lblActiveSelect"]
                    : arPhrase["lblActiveSelect"]}
                </span>

                <span>{selectedRequests.length}</span>

                <span>
                  {lang === "en"
                    ? enPhrase["lblActiveSelectText"]
                    : arPhrase["lblActiveSelectText"]}
                </span>

                <span>{requestsList.length}</span>
              </div>

              <button
                type="button"
                onClick={handleToggleSelectAll}
                disabled={!requestsList.length}
                className="font-Almarai text-[12px] leading-[24px] text-[#B68A35] cursor-pointer select-none disabled:cursor-not-allowed disabled:opacity-50"
              >
                {selectedRequests.length === requestsList.length &&
                requestsList.length > 0
                  ? lang === "en"
                    ? enPhrase["lblUnSelectAll"]
                    : arPhrase["lblUnSelectAll"]
                  : lang === "en"
                    ? enPhrase["lblSelectAll"]
                    : arPhrase["lblSelectAll"]}
              </button>
            </div>

            <InvoicesWithRequstsSection
              enPhrase={enPhrase}
              arPhrase={arPhrase}
              lang={lang}
              requests={requestsList}
              selectedRequests={selectedRequests}
              onToggleRequest={handleToggleRequest}
              getRequestUniqueKey={getRequestUniqueKey}
            />
          </div>

          <InvoicesPaymentSection
            service={service}
            enPhrase={enPhrase}
            arPhrase={arPhrase}
            lang={lang}
            requests={service?.data}
            servicesList={requestsList}
            selectedRequests={selectedRequests}
            getRequestUniqueKey={getRequestUniqueKey}
            caseid={caseid}
            onPaymentSuccessClose={handlePaymentSuccessClose}
          />
        </div>
      </div>
    </ThemeLayout>
  );
};

export default InvoiceDetails;
